import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { forwardRef, memo } from 'react';
import { PortalToBody } from '@deliveryhero/armor-system';
import { useDatePickerClassNames } from '../hooks/useDatePickerClassNames';
import { useDateRangePicker } from './hooks/useDateRangePicker';
import { DateRangePickerRoot } from './style';
import { DatePickerInput, DatePickerInputIcon, DatePickerDropdown, DatePickerDropdownArrow } from '../style';
import { DATE_RANGE_PICKER_CLASS_PREFIX } from '../constants';
import { DatePickerActionBar } from '../DatePickerActionBar';
import { DatePickerMonthYearSelector } from '../DatePickerMonthYearSelector';
import { DatePickerDaySelector } from '../DatePickerDaySelector';
import { DatePickerTopBar } from '../DatePickerTopBar';
import { DatePickerTimeSelector } from '../DatePickerTimeSelector';
import { DatePickerHeadSlot } from '../DatePickerHeadSlot';
/**
 * # DateRangePicker
 *
 * ## [Documentation](https://armor.deliveryhero.com/251886272/p/489489-date-range-picker/b/65de07)
 *
 * ## Examples
 *
 * ***
 *
 * ```
 * import { useState } from 'react';
 * import { DateRangePicker } from '@deliveryhero/armor-datepicker';
 *
 * const [value, setValue] = useState<[Date, Date]>();
 *
 * <DateRangePicker dateValue={value} onDateValueChange={setValue} />
 * ```
 * ***
 *
 * @armor-docs-component
 */
export var DateRangePicker = forwardRef(function DateRangePicker(_ref, ref) {
  var className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var classNameComponents = useDatePickerClassNames(DATE_RANGE_PICKER_CLASS_PREFIX, className);
  var _useDateRangePicker = useDateRangePicker(props, ref),
    rootProps = _useDateRangePicker.rootProps,
    inputProps = _useDateRangePicker.inputProps,
    inputMaskProps = _useDateRangePicker.inputMaskProps,
    portalProps = _useDateRangePicker.portalProps,
    dropdownProps = _useDateRangePicker.dropdownProps,
    arrowProps = _useDateRangePicker.arrowProps,
    topBarProps = _useDateRangePicker.topBarProps,
    daySelectorProps = _useDateRangePicker.daySelectorProps,
    monthYearSelectorProps = _useDateRangePicker.monthYearSelectorProps,
    actionBarProps = _useDateRangePicker.actionBarProps,
    timeSelectorProps = _useDateRangePicker.timeSelectorProps,
    displayMonthYearSelector = _useDateRangePicker.displayMonthYearSelector,
    open = _useDateRangePicker.open,
    showActions = _useDateRangePicker.showActions,
    showTimePicker = _useDateRangePicker.showTimePicker,
    showMaskedInput = _useDateRangePicker.showMaskedInput,
    shouldShowHeadSlot = _useDateRangePicker.shouldShowHeadSlot,
    headSlotProps = _useDateRangePicker.headSlotProps;
  return React.createElement(DateRangePickerRoot, _objectSpread(_objectSpread({}, rootProps), {}, {
    className: classNameComponents.Root
  }), React.createElement(DatePickerInput, _objectSpread(_objectSpread({}, showMaskedInput ? inputMaskProps : inputProps), {}, {
    type: "text",
    className: classNameComponents.Input,
    after: React.createElement(DatePickerInputIcon, null),
    ref: inputProps.ref
  })), open && React.createElement(PortalToBody, _objectSpread({}, portalProps), React.createElement(DatePickerDropdown, _objectSpread(_objectSpread({}, dropdownProps), {}, {
    className: classNameComponents.Dropdown
  }), shouldShowHeadSlot && React.createElement(DatePickerHeadSlot, _objectSpread({}, headSlotProps)), React.createElement(DatePickerTopBar, _objectSpread({}, topBarProps)), displayMonthYearSelector && React.createElement(DatePickerMonthYearSelector, _objectSpread({}, monthYearSelectorProps)), !displayMonthYearSelector && React.createElement(DatePickerDaySelector, _objectSpread({}, daySelectorProps)), showTimePicker && React.createElement(DatePickerTimeSelector, _objectSpread({}, timeSelectorProps)), showActions && React.createElement(DatePickerActionBar, _objectSpread({}, actionBarProps)), React.createElement(DatePickerDropdownArrow, _objectSpread({}, arrowProps)))));
});
DateRangePicker.defaultProps = {};
/** prop-types are required here for run-time checks */

export var MemoizedDateRangePicker = memo(DateRangePicker);